import { Alert } from '@mm/components';
import {
  MAX_ALLOWED_WIDGET_ITEMS,
  MAX_TEXT_WIDGET_LENGTH,
} from '@mm/constants/widgets';
import { useResolutionInfo } from '@mm/hooks';
import { ButtonType, PageButtonConfig } from '@mm/state/model/ButtonModel';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { showMMSMessageWarning } from '../../../editor/features/startBotPromoters/common/validateSmsRelatedWidgets';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import { PrimaryToolTip } from '../../components/PrimaryToolTip/PrimaryToolTip';
import { useTextWidgetHandlers } from '../../helpers/widget-helpers/use-text-widget-handlers';
import { ActivePathSelection } from '../common/ActivePathSelection/ActivePathSelection';
import { BubbleTextInput } from '../common/BubbleTextInput/BubbleTextInput';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { SmsWarningAlert } from '../common/SmsWarningAlert/SmsWarningAlert';
import { useEditMode } from '../common/use-edit-mode';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import { CreateEditButtonArea } from './CreateEditButtonArea/CreateEditButtonArea';
import { TextButtonsList } from './TextButtonsList/TextButtonsList';
import './TextWidget.pcss';

type Props = {
  widget: WidgetModel;
  chatbotId: number;
  disableDragging?: boolean;
  disableDeleting?: boolean;
  disableConditionalLogic?: boolean;
  forAds?: boolean;
  draggableProvided?: DraggableProvided;
  disabledButtonTypes?: ButtonType[];
  isSmsRelated?: boolean;
  showOnlyNameAttributes?: boolean;
  showAttributePicker?: boolean;
  forInstagram?: boolean;
  platforms?: PlatformType[];
  showCountByChannel?: boolean;
  smsOnly?: boolean;
  showAttributesStartWarning?: boolean;
  usedInSlideout?: boolean;
  widgetsToExclude?: WidgetTypes[];
  pageWidgets: WidgetModel[];
} & WidgetContainerRefProp;

const AddButton = ({
  onClick,
  disabled,
}: {
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={classnames('text-widget__add-button', {
        'text-widget__add-button--disabled': disabled,
      })}
      onClick={onClick}
    >
      + Add Button
    </div>
  );
};

// tslint:disable-next-line:cyclomatic-complexity
export const TextWidget = (props: Props) => {
  const {
    widget,
    chatbotId,
    disableDragging,
    disableDeleting,
    disableConditionalLogic,
    forAds,
    draggableProvided,
    isSmsRelated,
    showAttributePicker = true,
    showAttributesStartWarning = false,
    showOnlyNameAttributes = false,
    disabledButtonTypes,
    forInstagram,
    showCountByChannel,
    smsOnly,
    usedInSlideout,
    widgetsToExclude,
    pageWidgets,
    platforms,
  } = props;
  const textWidgetHandlers = useTextWidgetHandlers();

  const {
    setActiveNode,
    getActiveButtonFromWidgetButtons,
    removeNodeAndSuccessors,
  } = useContext(PathGraphContext);

  const { editMode, setEditMode } = useEditMode(props);
  const [showAddButton, setShowAddButton] = useState(false);
  const [openedButtonId, setOpenedButtonId] = useState<number | undefined>();
  const [isVisibleSmsWarningAlert, setIsVisibleSmsWarningAlert] =
    useState(false);
  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);

  const showMMSWarning =
    isSmsRelated && showMMSMessageWarning(pageWidgets, widget?.id);

  useEffect(() => {
    setIsVisibleSmsWarningAlert(false);

    if (widget.config.body) {
      if (widget.config.body.length > 160 && showMMSWarning) {
        setIsVisibleSmsWarningAlert(true);
      }
      setHasAttributesInBody(/{{.+}}/.test(widget.config.body));
    }
  }, [widget.config.body, showMMSWarning]);
  const loadedAndNotDeletedDialogueIds = useSelector((state: StoreState) =>
    state.page.entities.map(d => d.id),
  );

  const tooltipText =
    isSmsRelated &&
    `Text widget buttons not supported on SMS. 
      Use a Quick Question instead`;

  const exceptionInstagramButtonTypes = [
    ButtonType.CALL,
    ButtonType.SHARE,
    ButtonType.EMAIL,
    ButtonType.PHONE_NUMBER,
    ButtonType.LOCATION,
    ButtonType.HANDOVER,
    ButtonType.OPT_IN,
  ];

  const showTooltip = isSmsRelated && !!tooltipText;

  const disabledButtonTypesMapped = useMemo<ButtonType[]>(() => {
    return [
      ...(disabledButtonTypes || []),
      ...(forInstagram ? exceptionInstagramButtonTypes : []),
    ];
  }, [forInstagram, disabledButtonTypes]);

  const activeButton = useMemo(() => {
    return getActiveButtonFromWidgetButtons?.(widget.buttons);
  }, [getActiveButtonFromWidgetButtons, widget]);

  const beforeDestroy = useCallback(() => {
    if (openedButtonId) {
      setActiveNode?.({
        id: openedButtonId,
        type: 'button',
        action: 'close',
      });
    }
    removeNodeAndSuccessors?.({
      id: widget.id,
      type: 'widget',
      actionType: 'delete',
      parentId: widget.pageId,
      parentType: 'dialogue',
    });
  }, [widget.id, openedButtonId]);

  const onClickAddNew = () => {
    setShowAddButton(true);
    setOpenedButtonId(undefined);
  };

  const onTextInputClick = () => setEditMode(true);

  // Note: this is FE validation/injection of errors due to BE failing to keep dialogues deletion
  // propagates to all affected entities.
  const widgetWithFEInjectedErrors = useMemo(() => {
    const buttonsWithDeletedDialogues = widget.buttons
      .filter(b => b.type === ButtonType.PAGE)
      .filter(
        b =>
          !loadedAndNotDeletedDialogueIds.includes(
            (b.config as PageButtonConfig).pageId,
          ),
      );

    const errorMessageTexts = buttonsWithDeletedDialogues.map(
      b =>
        `Dialogue with id ${(b.config as PageButtonConfig).pageId} for button ${
          b.name
        } has been deleted`,
    );

    const errorMessages = errorMessageTexts.map(m => ({
      field: '#/buttons',
      rule: {
        type: 'button_dialogue_deleted',
        arguments: m,
      },
    }));

    return {
      ...widget,
      validated: {
        ...widget.validated,
        buttonDialogueInValid: !!errorMessages.length,
      },
      validationMessages: [
        ...(widget.validationMessages || []),
        ...errorMessages,
      ],
    };
  }, [widget, loadedAndNotDeletedDialogueIds]);
  const { isDesktop } = useResolutionInfo();

  return (
    <div className="text-widget">
      {isSmsRelated && (
        <ErrorPortal widget={widget}>
          <SmsWarningAlert
            isVisible={isVisibleSmsWarningAlert}
            setIsVisibleSmsWarningAlert={setIsVisibleSmsWarningAlert}
          />
        </ErrorPortal>
      )}
      <div className="text-widget__main-area-wrapper">
        <FacebookPageIcon />

        <div className="text-widget__right-area">
          <div className="text-widget__right-top-area">
            <OnClickElsewhere
              className="text-widget__text-area-wrapper"
              handleClick={() => setEditMode(false)}
            >
              <BubbleTextInput
                className="text-widget__bubble-text-input"
                updateText={newText =>
                  textWidgetHandlers.updateText(widget, newText)
                }
                value={widget.config.body}
                editMode={editMode}
                setEditMode={setEditMode}
                onClick={onTextInputClick}
                showAttributePicker={showAttributePicker}
                showOnlyNameAttributes={showOnlyNameAttributes}
                maxLength={MAX_TEXT_WIDGET_LENGTH}
                newCharacterCountDesign
                showCountByChannel={showCountByChannel}
                maxSmsChannelTextLength={MAX_TEXT_WIDGET_LENGTH}
                maxOthersChannelTextLength={MAX_TEXT_WIDGET_LENGTH}
              />
            </OnClickElsewhere>

            <WidgetControls
              widget={widgetWithFEInjectedErrors}
              disableDragging={disableDragging}
              disableDeleting={disableDeleting}
              disableConditionalLogic={disableConditionalLogic}
              beforeDestroy={beforeDestroy}
              draggableProvided={draggableProvided}
              platforms={platforms}
            />

            {!showAttributePicker &&
              hasAttributesInBody &&
              showAttributesStartWarning && (
                <ErrorPortal widget={widget}>
                  <div className="text-widget__alert-container">
                    <Alert
                      className="text-widget__warning"
                      type={'error'}
                      showCloseIcon={false}
                    >
                      Attributes are not available on the first widget since we
                      only get contact’s profile AFTER their first response.
                    </Alert>
                  </div>
                </ErrorPortal>
              )}

            {isSmsRelated && !widget.supportedByPlatforms.sms && (
              <ErrorPortal widget={widget}>
                <WidgetUnsupportedError
                  className="text-widget__warning"
                  isError={smsOnly}
                  type="sms"
                >
                  Text with buttons are not supported on SMS. Only the text in
                  this widget will be sent on SMS
                </WidgetUnsupportedError>
              </ErrorPortal>
            )}
          </div>

          <div className="text-widget__buttons-area">
            <TextButtonsList
              disabledButtonTypes={disabledButtonTypesMapped}
              chatbotId={chatbotId}
              widget={widget}
              forAds={forAds}
              forInstagram={forInstagram}
              openedButtonId={openedButtonId}
              setOpenedButtonId={setOpenedButtonId}
              usedInSlideout={usedInSlideout}
            />

            {widget.buttons.length < MAX_ALLOWED_WIDGET_ITEMS.text && (
              <div className="text-widget__add-button-wrapper">
                {showAddButton && (
                  <CreateEditButtonArea
                    disabledButtonTypes={disabledButtonTypesMapped}
                    forAds={forAds}
                    chatbotId={chatbotId}
                    widget={widget}
                    closeButtonArea={() => setShowAddButton(false)}
                    usedInSlideout={usedInSlideout}
                  />
                )}

                {showTooltip ? (
                  <PrimaryToolTip
                    useHover={true}
                    anchorElement={<AddButton disabled={true} />}
                  >
                    {tooltipText}
                  </PrimaryToolTip>
                ) : (
                  <AddButton onClick={onClickAddNew} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {activeButton && (
        <ActivePathSelection
          chatbotId={chatbotId}
          widget={widget}
          activeButton={activeButton}
          widgetsToExclude={widgetsToExclude}
          platforms={platforms}
          usedInSlideout={usedInSlideout}
        />
      )}
    </div>
  );
};
